import React, { useState, useEffect } from 'react';
import { addDoc, collection, getDocs, query } from 'firebase/firestore';
import { db } from '../services/firebase';
import SEO from '../components/SEO';
import './About.css';

import VeroImage from '../assets/images/Vero.jpg';
import RoseImage from '../assets/images/Rose.jpg';

import { useTranslation } from 'react-i18next';
import { pickL } from '../utils/i18nContent';

const About = () => {
  const { t, i18n } = useTranslation('about');

  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');

  // >>> plus de "subject" ici
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    contactType: 'general',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        const q = query(collection(db, 'dance_courses'));
        const qs = await getDocs(q);
        const list = qs.docs.map(d => ({ id: d.id, ...d.data() }));
        setCourses(list);
      } catch (err) {
        console.error('Erreur de chargement des cours:', err);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        courseId: selectedCourse || null,
        lang: (i18n.language || 'fr').slice(0, 2),
        createdAt: new Date(),
        status: 'new',
      });
      setSubmitSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        contactType: 'general',
      });
      setSelectedCourse('');
    } catch (error) {
      console.error("Erreur d'envoi du message:", error);
      alert(t('contact.errors.sendFail'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const schoolLocation = {
    address: 'Rue Égide Van Ophem 46, 1180 Uccle',
    lat: 50.8036,
    lng: 4.3376,
  };
  const encodedAddress = encodeURIComponent(schoolLocation.address);
  const googleMapsDirections = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  const wazeDirections = `https://www.waze.com/ul?q=${encodedAddress}&navigate=yes`;
  const appleMapsDirections = `https://maps.apple.com/?address=${encodedAddress}`;

  return (
    <div className="about-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
      />

      <div className="container">
        <h1 className="section-title">{t('title')}</h1>

        {/* Présentation */}
        <section className="about-block">
          <h2>{t('history.title')}</h2>
          <p>{t('history.p1')}</p>
          <p>{t('history.p2')}</p>
        </section>

        <section className="about-block">
          <h2>{t('mission.title')}</h2>
          <p>{t('mission.p1')}</p>
        </section>

        <section className="about-block">
          <h2>{t('method.title')}</h2>
          <p>{t('method.intro')}</p>
          <p>{t('method.p2')}</p>
          <ul className="feature-list">
            <li>{t('method.features.0')}</li>
            <li>{t('method.features.1')}</li>
            <li>{t('method.features.2')}</li>
            <li>{t('method.features.3')}</li>
            <li>{t('method.features.4')}</li>
          </ul>
        </section>

        <section className="team-section">
          <h2 className="section-title">{t('team.title')}</h2>
          <div className="team-grid">
            <div className="team-member" itemScope itemType="http://schema.org/Person">
              <div className="member-photo">
                <img src={VeroImage} alt="Véro Mukendi" />
              </div>
              <h3 itemProp="name">Véro Mukendi</h3>
              <p itemProp="jobTitle">{t('team.veroTitle')}</p>
            </div>

            <div className="team-member" itemScope itemType="http://schema.org/Person">
              <div className="member-photo">
                <img src={RoseImage} alt="Rose Mukendi" />
              </div>
              <h3 itemProp="name">Rose Mukendi</h3>
              <p itemProp="jobTitle">{t('team.roseTitle')}</p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="contact-section">
          <h2 className="section-title">{t('contact.title')}</h2>

          <div className="contact-grid">
            {/* Colonne gauche : carte */}
            <div className="contact-map">
              <h3 style={{ color: '#7C3AED', marginBottom: '15px', fontSize: '1.3rem' }}>
                {t('contact.mapTitle')}
              </h3>

              <div className="map-container">
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '12px' }}
                  src={`https://maps.google.com/maps?q=${schoolLocation.lat},${schoolLocation.lng}&z=15&output=embed`}
                  allowFullScreen
                  title={t('contact.mapIframeTitle')}
                />
                <div className="map-overlay">
                  <div className="location-info">
                    <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1rem' }}>MKD Studio</p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem' }}>{schoolLocation.address}</p>
                    <div className="map-links">
                      <a className="map-link google-maps" href={googleMapsDirections} target="_blank" rel="noopener noreferrer">
                        {t('contact.directions.google')}
                      </a>
                      <a className="map-link waze" href={wazeDirections} target="_blank" rel="noopener noreferrer">
                        {t('contact.directions.waze')}
                      </a>
                      <a className="map-link apple-maps" href={appleMapsDirections} target="_blank" rel="noopener noreferrer">
                        {t('contact.directions.apple')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-info">
                <h3 style={{ color: '#7C3AED', marginBottom: '12px', fontSize: '1.3rem' }}>
                  {t('contact.coordsTitle')}
                </h3>
                <p style={{ fontSize: '.95rem', lineHeight: 1.5 }}>
                  <strong>{t('contact.labels.email')}:</strong> info@mkdstudio.co<br />
                  <strong>{t('contact.labels.phone')}:</strong> +32 493 88 72 88<br />
                  <strong>{t('contact.labels.hours')}:</strong> {t('contact.hoursValue')}
                </p>
              </div>
            </div>

            {/* Colonne droite : formulaire */}
            <div className="contact-form">
              <h3>{t('contact.formTitle')}</h3>

              {submitSuccess ? (
                <div className="success-message">{t('contact.success')}</div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form-fields" noValidate>
                  {/* Type de contact */}
                  <div className="form-group">
                    <label htmlFor="contactType">
                      {t('contact.fields.contactType')}<span className="required">*</span>
                    </label>
                    <select
                      id="contactType"
                      name="contactType"
                      value={formData.contactType}
                      onChange={handleChange}
                      required
                    >
                      <option value="general">{t('contact.types.general')}</option>
                      <option value="course">{t('contact.types.course')}</option>
                      <option value="registration">{t('contact.types.registration')}</option>
                      <option value="other">{t('contact.types.other')}</option>
                    </select>
                  </div>

                  {/* Sélecteur cours si nécessaire */}
                  {formData.contactType === 'course' && (
                    <div className="form-group">
                      <label htmlFor="course">
                        {t('contact.fields.courseLabel')}<span className="required">*</span>
                      </label>
                      <select
                        id="course"
                        name="course"
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                        required
                      >
                        <option value="">{t('contact.fields.coursePlaceholder')}</option>
                        {loadingCourses
                          ? <option>{t('contact.loadingCourses')}</option>
                          : courses.map(course => (
                              <option key={course.id} value={course.id}>
                                {pickL(course.title_i18n ?? course.title, i18n.language, 'fr')}
                              </option>
                            ))
                        }
                      </select>
                    </div>
                  )}

                  {/* Identité */}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">{t('contact.fields.firstName')}<span className="required">*</span></label>
                      <input
                        id="firstName"
                        type="text"
                        name="firstName"
                        placeholder={t('contact.fields.firstName') + ' *'}
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">{t('contact.fields.lastName')}<span className="required">*</span></label>
                      <input
                        id="lastName"
                        type="text"
                        name="lastName"
                        placeholder={t('contact.fields.lastName') + ' *'}
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  {/* Coordonnées */}
                  <div className="form-group">
                    <label htmlFor="email">{t('contact.fields.email')}<span className="required">*</span></label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      placeholder={t('contact.fields.email') + ' *'}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">{t('contact.fields.phone')}<span className="required">*</span></label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      placeholder={t('contact.fields.phone') + ' *'}
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                    />
                  </div>

                  {/* Message */}
                  <div className="form-group">
                    <label htmlFor="message">{t('contact.fields.message')}<span className="required">*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder={t('contact.fields.message') + ' *'}
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      required
                    />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="submit-button">
                    {isSubmitting ? t('contact.sending') : t('contact.submit')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;